import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs } from '../../../Common/Layout';
import { useBrandColor } from '../../../../common/Common/ThemeProvider';
import { useNavigation } from '../../../Common/useNavigation';
import { useParticipant } from '../../ParticipantProvider';
import { useRegistrationsUrlState } from '../helpers';
import GetEventQuery from '../../../../frontend/Common/GetEventQuery';
import GetRegistrationSummaryForDashboardQuery from '../GetRegistrationSummaryForDashboardQuery';
import UI from '../../../../common/UI';
import UpgradesForm from './UpgradesForm';
import useDocumentTitle from '../../../../common/useDocumentTitle';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

export interface UpgradesPageProps {
  participantId: string;
  eventId: string;
  token: string;
}

const UpgradesPage = ({
  participantId, eventId, token,
}: UpgradesPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const history = useHistory();
  const project = useProject();
  const { impersonating } = useParticipant();
  const [urlState] = useRegistrationsUrlState();

  const {
    data: summaryData, loading,
  } = useQuery(
    GetRegistrationSummaryForDashboardQuery,
    {
      variables: {
        participant: participantId,
        event: eventId,
        token,
        registrations_limit: urlState.registrations.limit,
        registration_filters: {
          assigned: true,
        },
        waitlist_registrations_limit: 0,
        teams_limit: 0,
        include_filtered_teams: false,
      },
      onError: (error) => {
        if (error.graphQLErrors?.length > 0) {
          // Redirect only in case of a normal authentication/validation error.
          history.replace(r('Portal'));
        }
      },
    },
  );

  useDocumentTitle([
    summaryData?.registrationSummary.participant.full_name,
    summaryData?.registrationSummary.event.title,
    'Atleta',
  ]);

  const themeInitialized = useBrandColor(summaryData?.registrationSummary.event.brand_color);

  const { data: eventData } = useQuery(
    GetEventQuery,
    {
      variables: {
        id: eventId,
        invitationCode: null,
        coupon: null,
      },
    },
  );

  if (!summaryData || !eventData || !themeInitialized) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <Breadcrumbs hideHome={impersonating}>
        <UI.Link to={r('Registrations', {
          projectId: project.id,
          eventId: summaryData.registrationSummary.event.id,
          participantId: summaryData.registrationSummary.participant.id,
          token: summaryData.registrationSummary.view_token,
        })}
        >
          {summaryData.registrationSummary.event.title}
        </UI.Link>
        <UI.Strong>{t('order_extras')}</UI.Strong>
      </Breadcrumbs>

      <UpgradesForm
        summary={summaryData.registrationSummary}
        loadingSummary={loading}
        event={eventData.event}
      />
    </>
  );
};

export default UpgradesPage;
